<template lang="pug">
div.neutral200
  v-container.pa-8(style="max-width: 900px;")
    div.d-flex.justify-center.mt-16(v-if="isFetching")
      v-progress-circular(:width="2" :size="50" color="primary" indeterminate)

    template(v-else)
      v-row(no-gutters align="center")
        v-col.shrink
          v-btn.ml-n3(text icon @click="goBack" height)
            v-icon(color="basic800" :size="24") mdi-arrow-left
        v-col.ml-3
          .text-2xl.font-semibold.neutral700--text {{ id ? 'Edit item':  'Add item' }}

      v-form(ref="form")
        //- Medication details
        v-card.pa-6.mt-4.round-xl(outlined)
          .text-md.font-semibold.neutral700--text Medication details
          template(v-if="isHqProduct")
            v-row.mt-3
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text {{ $t('name') }}
                div.text-sm.neutral700--text.mt-2 {{ medicine.name }}
                yz-badge.mt-1(size="sm" color="blue-100" text-color="blue-500" border) HQ
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Generic name
                div.text-sm.neutral500--text.mt-2 {{ medicine.generic_name || 'Not available' }}
            v-row.mt-3
              v-col(cols="4")
                div.text-sm.font-semibold.neutral500--text Category
                div.text-sm.neutral700--text.mt-2.text-capitalize {{ medicine.category }}
              v-col(cols="4")
                div.text-sm.font-semibold.neutral500--text Group
                div.text-sm.neutral700--text.mt-2 {{ groupNames }}
              v-col(cols="4")
                div.text-sm.font-semibold.neutral500--text SKU
                div.text-sm.neutral700--text.mt-2 {{ medicine.sku || '-' }}
          template(v-else)
            v-row.mt-3
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text {{ $t('name') }}
                v-text-field.text-sm.mt-2.round-xl(v-model="medicine.name" :placeholder="$t('name')" outlined dense height="48" :rules="rules.not_empty" hide-details="auto")
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Generic name
                v-text-field.text-sm.mt-2.round-xl(v-model="medicine.generic_name" placeholder="Generic name" outlined dense height="48" hide-details="auto")
              v-col(cols="4")
                div.text-sm.font-semibold.neutral500--text Category
                y-select.mt-2(v-model="medicine.category" placeholder="Category" attach :items="[{ text: 'Medication', value: 'medication' }, { text: 'Equipment', value: 'equipment' }, { text: 'Consumable', value: 'consumable' }]" item-text="text" item-value="value" :rules="rules.not_empty_select")
              v-col(cols="4")
                div.text-sm.font-semibold.neutral500--text Group
                medication-group-dropdown.mt-2(v-model="medicine.groups" attach :error="false")
              v-col(cols="4")
                div.text-sm.font-semibold.neutral500--text SKU
                medication-sku-dropdown.mt-2(v-model="medicine.sku" :error="false")

        //- Dosage and instructions
        v-card.pa-6.mt-4.round-xl(outlined)
          div.d-flex.justify-space-between
            div
              .text-md.font-semibold.neutral700--text Dosage and instructions
              .text-sm.neutral700--text.mt-2 Set default value for dosage and instructions. This will be a suggestion value to providers.
              .text-sm.neutral700--text They can change it accordingly while dispensing the medication to patients.
              .text-sm.neutral700--text Disable if your item does not need one.
            div(v-if="!isHqProduct")
              y-switch.mb-n3.mt-2(v-model="instructionEnabled")
          template(v-if="isHqProduct")
            v-row.mt-3(v-if="instructionEnabled")
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Dosage amount
                div.text-sm.neutral700--text.mt-2 {{ medicine.dosage_value || '-' }}
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Dosage unit
                div.text-sm.neutral700--text.mt-2 {{ medicine.dosage_unit || '-' }}
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Instruction
                div.text-sm.neutral700--text.mt-2 {{ medicine.instruction || '-' }}
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Precaution
                div.text-sm.neutral700--text.mt-2 {{ medicine.precaution || '-' }}
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Frequency
                div.text-sm.neutral700--text.mt-2 {{ medicine.frequency || '-' }}
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Duration
                div.text-sm.neutral700--text.mt-2 {{ medicine.duration || '-' }}
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Indication
                div.text-sm.neutral700--text.mt-2 {{ medicine.indication || '-' }}
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Dispense quantity
                div.text-sm.neutral700--text.mt-2 {{ medicine.default_qty || '-' }}
          template(v-else)
            v-row.mt-3(v-if="instructionEnabled")
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Dosage amount
                v-text-field.text-sm.mt-2.round-xl(v-model="medicine.dosage_value" placeholder="Dosage amount" outlined dense type="number" height="48" hide-details="auto")
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Dosage unit
                medication-dosage-unit-dropdown.mt-2(v-model="medicine.dosage_unit" :error="false")
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Instruction
                medication-instruction-dropdown.mt-2(v-model="medicine.instruction" :error="false")
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Precaution
                medication-precaution-dropdown.mt-2(v-model="medicine.precaution" :error="false")
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Frequency
                medication-frequency-dropdown.mt-2(v-model="medicine.frequency" :error="false")
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Duration
                medication-duration-dropdown.mt-2(v-model="medicine.duration" :error="false")
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Indication
                medication-indication-dropdown.mt-2(v-model="medicine.indication" :error="false")
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Dispense quantity
                v-text-field.text-sm.mt-2.round-xl(v-model="medicine.default_qty" placeholder="Default usage amount" outlined dense type="number" :rules="id ? rules.not_empty : []" height="48" hide-details="auto")

        //- Unit of measurement conversion
        v-card.pa-6.mt-4.round-xl(outlined)
          div.d-flex.justify-space-between
            div
              .text-md.font-semibold.neutral700--text Unit of measurement conversion
              .text-sm.neutral700--text.mt-2 Set how this product’s stock unit converts into its dispense unit. Enable this only if the stock UOM is different from how the item is dispensed or sold. The system will calculate quantities based on the conversion you set.
            div(v-if="!isHqProduct")
              y-switch.mb-n3.mt-2(v-model="uomConversionEnabled")
          div.mt-4(v-if="uomConversionEnabled")
            div(v-if="uomConversions.length > 0")
              v-simple-table
                template(v-slot:default)
                  thead
                    tr
                      th.text-sm.font-semibold.neutral700--text UOM
                      th.text-sm.font-semibold.neutral700--text Convert to
                      th.text-sm.font-semibold.neutral700--text Multiply by
                      th.text-sm.font-semibold.neutral700--text Summary
                  tbody
                    tr(v-for="(conv, index) in uomConversions" :key="index")
                      td.py-3
                        .text-sm.neutral700--text {{ conv.uom }}
                      td.py-3
                        .text-sm.neutral700--text {{ conv.convert_to }}
                      td.py-3
                        .text-sm.neutral700--text {{ conv.multiplier }}
                      td.py-3
                        .text-sm 1 {{ conv.uom }} = {{ conv.multiplier }} {{ conv.convert_to }}
              div.mt-3(v-if="!isHqProduct")
                y-button(label="Edit conversions" size="sm" outlined @click="onAddUomConversion")
            div(v-else)
              div.empty-box.pa-8(align="center")
                v-avatar(color="neutral300" size="54")
                  v-icon(color="neutral500" size="30") mdi-calculator-variant-outline
                div.text-center.text-md.font-semibold.neutral700--text.mt-6 No converted UOMs
                div.text-center.text-sm.font-normal.neutral500--text.mt-1 Any converted UOMs will appear in here
                y-button.mt-6(label="Add UOM conversion" size="sm" color="neutral700" text-color="neutral100" @click="onAddUomConversion")

        //- Pricing
        v-card.pa-6.mt-4.round-xl(outlined)
          .text-md.font-semibold.neutral700--text Pricing
          div.d-flex.align-center.mt-2
            .text-sm.neutral700--text.mr-2 Set default pricing tier for this item. You can manage your pricing tier in settings.
            y-button(label="Go to pricing tier setting" size="xs" color="transparent" text-color="neutral700" icon-color="neutral700" icon-size="15" underline button-class="px-0" append-icon="mdi-arrow-top-right" @click="goToPriceTiers")

          //- HQ default pricing (read-only)
          template(v-if="isHqProduct && hqPriceTiers.length > 0")
            .yz-heading-9.text-neutral-700.mt-6 Default pricing
            .yz-regular-body-sm.mt-1 This pricing has been set by HQ and shared across all branches.
            yz-simple-table.mt-3
              template(v-slot:default)
                thead
                  tr
                    th.yz-medium-body-xs.text-neutral-700 Price tier
                    th.yz-medium-body-xs.text-neutral-700 Payment method
                    th.yz-medium-body-xs.text-neutral-700(style="width: 350px;") Default price
                tbody
                  template(v-for="hqTier in hqPriceTiers")
                    tr(:key="'hq-' + hqTier.price_tier.id")
                      td.py-3
                        .yz-heading-9.text-neutral-700 {{ hqTier.price_tier.name }}
                        .yz-regular-body-sm {{ hqTier.price_tier.description }}
                      td.py-3
                        .d-flex.align-center.gap-2
                          div
                            .yz-regular-body-xs.text-neutral-700 Panel: {{ getHqTierPanelCount(hqTier.price_tier) }}
                            .yz-regular-body-xs.text-neutral-700.mt-1 Self-Pay: {{ getHqTierSelfPayCount(hqTier.price_tier) }}
                          yz-icon.text-neutral-500.cursor-pointer(size="16" @click="openDetails(hqTier.price_tier)") mdi-information-outline
                      td.py-3
                        template(v-if="hasActiveUomConversions && hqTier.price_per_uom && hqTier.price_per_uom.length > 0")
                          .d-flex.align-center.justify-space-between.cursor-pointer(@click="$set(hqTier, 'expanded', !hqTier.expanded)")
                            .yz-regular-body-sm.text-neutral-500.font-italic Multiple UOMs
                            yz-icon(size="16") {{ hqTier.expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                        template(v-else)
                          .yz-regular-body-sm.text-neutral-700 {{ $currency.symbol }} {{ (hqTier.price / $currency.multiplier).toFixed(2) }}
                    template(v-if="hasActiveUomConversions && hqTier.expanded && hqTier.price_per_uom")
                      tr(v-for="(uomPrice, uIdx) in hqTier.price_per_uom" :key="'hq-uom-' + hqTier.price_tier.id + '-' + uIdx")
                        td
                        td
                        td.py-2
                          .d-flex.align-center.gap-2
                            .yz-medium-body-sm.text-neutral-700(style="min-width: 80px;") {{ uomPrice.uom }}
                            .yz-regular-body-sm.text-neutral-700 {{ $currency.symbol }} {{ (uomPrice.price / $currency.multiplier).toFixed(2) }}

            //- Branch override pricing
            div.d-flex.align-start.justify-space-between.mt-6
              div
                .text-sm.font-semibold.neutral700--text Add default pricing override
                .text-sm.neutral500--text.mt-1 This pricing will override the default pricing and adjusted according to your selected branches and pricing setup.
              y-button(v-if="canEditPriceOverride" label="Edit" size="sm" outlined @click="onEditOverride")

            template(v-if="overridePriceTiers.length > 0")
              v-simple-table.mt-2
                template(v-slot:default)
                  thead
                    tr
                      th.text-sm.font-semibold.neutral700--text Price tier
                      th.text-sm.font-semibold.neutral700--text Payment method
                      th.text-sm.font-semibold.neutral700--text Override price
                  tbody
                    template(v-for="(oTier, oIdx) in formattedOverrideTiers")
                      tr(:key="'override-' + oIdx")
                        td.py-3
                          .text-sm.font-semibold.neutral700--text {{ oTier.name }}
                          .text-sm.neutral700--text {{ oTier.description }}
                        td.py-3
                          div.d-flex.align-center
                            div
                              .text-xs.neutral700--text Panel: {{ oTier.panelText }}
                              .text-xs.neutral700--text.mt-1 Self-Pay: {{ oTier.selfPayText }}
                            v-btn.ml-3(icon small @click="openDetails(oTier.tierObj)")
                              v-icon(size="20") mdi-information-outline
                        td.py-3
                          template(v-if="hasActiveUomConversions && oTier.price_per_uom && oTier.price_per_uom.length > 0")
                            div.d-flex.align-center.justify-space-between.cursor-pointer(@click="$set(overridePriceTiers[oIdx], 'expanded', !overridePriceTiers[oIdx].expanded)")
                              .text-sm.neutral500--text.font-italic Multiple UOMs
                              v-icon(size="16") {{ oTier.expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                          template(v-else)
                            .text-sm.neutral700--text {{ formatOverridePrice(oTier.price) }}
                      template(v-if="hasActiveUomConversions && oTier.expanded && oTier.price_per_uom")
                        tr(v-for="(uomPrice, uIdx) in oTier.price_per_uom" :key="'override-uom-' + oIdx + '-' + uIdx")
                          td
                          td
                          td.py-2
                            div.d-flex.align-center
                              .text-sm.font-semibold.neutral700--text.mr-3(style="min-width: 80px;") {{ uomPrice.uom }}
                              .text-sm.neutral700--text {{ formatOverridePrice(uomPrice.price) }}
            template(v-else)
              div.text-sm.neutral500--text.mt-2.font-italic No override pricing set

          //- Non-HQ pricing table
          template(v-if="!isHqProduct")
            template(v-if="pricingTiers.length === 0 && !isFetchingPricingTiers")
              div.empty-box.mt-6.pa-8(align="center")
                v-avatar(color="neutral300" size="54")
                  v-icon(color="neutral500" size="30") mdi-tag-outline
                div.text-center.text-md.font-semibold.neutral700--text.mt-6 You don't have any pricing tier yet
                div.text-center.text-sm.font-normal.neutral500--text.mt-1 Set your pricing tier now
                y-button.mt-6(label="Set your pricing tier" size="sm" color="neutral700" text-color="neutral100" icon-color="neutral100" icon-size="20" append-icon="mdi-arrow-top-right" @click="goToPriceTiers")
            v-simple-table.mt-2(v-else)
              template(v-slot:default)
                thead
                  tr
                    th.text-table-header Name
                    th.text-table-header Payment methods
                    th.text-table-header(style="width: 350px;") Price
                tbody
                  template(v-for="tier in pricingTiers")
                    tr(:key="tier.id")
                      td.py-2
                        .text-sm.font-semibold.neutral700--text {{ tier.name }}
                        .text-sm.neutral700--text {{ tier.description }}
                      td.py-2
                        div.d-flex.align-center
                          div
                            .text-xs.neutral700--text Panel: {{ getPanelCount(tier.panels) }}
                            .text-xs.neutral700--text.mt-1 Self-Pay: {{ getSelfPayCount(tier.self_pay) }}
                          v-btn.ml-3(icon small @click="openDetails(tier)")
                            v-icon(size="20") mdi-information-outline
                      td.py-2
                        template(v-if="!hasActiveUomConversions")
                          div(style="width: 150px;")
                            v-text-field.text-sm.my-2.round-xl(v-model="tier.price" placeholder="0.00" outlined dense height="48" :prefix="$currency.symbol" :rules="tier.required ? rules.not_empty : []" type="number" :background-color="allowedPriceEdit ? '' : 'neutral300'" :disabled="!allowedPriceEdit" hide-details)
                        template(v-else)
                          div.d-flex.align-center.justify-space-between.cursor-pointer(@click="$set(tier, 'expanded', !tier.expanded)")
                            .text-sm.neutral500--text.font-italic Multiple UOMs
                            v-icon(size="16") {{ tier.expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                    template(v-if="hasActiveUomConversions && tier.expanded")
                      tr(v-for="(uomPrice, uIdx) in tier.price_per_uom" :key="tier.id + '-uom-' + uIdx")
                        td
                        td
                        td.py-2
                          div.d-flex.align-center
                            .text-sm.font-semibold.neutral700--text.mr-3(style="min-width: 80px;") {{ uomPrice.uom }}
                            div(style="width: 150px;")
                              v-text-field.text-sm.round-xl(v-model="uomPrice.price" placeholder="0.00" outlined dense height="48" :prefix="$currency.symbol" type="number" :disabled="!allowedPriceEdit" hide-details)

        //- Stock
        v-card.pa-6.mt-4.round-xl(outlined)
          div.d-flex.align-center
            .text-md.font-semibold.neutral700--text Stock details
            v-spacer
            y-button(v-if="id" label="View stock history" size="xs" color="transparent" text-color="neutral700" icon-color="neutral700" icon-size="15" underline button-class="px-0" @click="goToStockHistory")
          v-row.mt-3(align="start")
            v-col(cols="6")
              div.d-flex(style="height: 48px;")
                div.button-box(style="min-width: 130px;")
                  div.d-flex.align-center.fill-height
                    div.text-sm.font-semibold.neutral700--text.mx-3.text-no-wrap Stock on hand
                v-text-field.text-sm(:value="stockSummaryDisplay.stockOnHand" placeholder="0" background-color="neutral300" disabled outlined dense height="48" hide-details style="border-radius: 0 10px 10px 0;")
                  template(#append v-if="id || (uomConversionEnabled && stockUomEquivalents.stockOnHand.length > 0)")
                    yz-tooltip.mr-1(v-if="uomConversionEnabled && stockUomEquivalents.stockOnHand.length > 0" direction="top" max-width="250")
                      yz-icon.mt-2.cursor-pointer(size="18") mdi-information-outline
                      template(#tooltip)
                        .yz-heading-9.white--text Equivalent to
                        ul.pl-4.mt-1
                          li.yz-regular-body-sm.white--text(v-for="(equiv, idx) in stockUomEquivalents.stockOnHand" :key="idx") {{ equiv }}
                    y-button.ml-3(v-if="id" label="Update" size="xs" outlined, background-color="white" @click="openUpdateStockDialog = true")
            //v-col.px-3(cols="4")
            //  div.d-flex.fill-height
            //    div.button-box(style="min-width: 110px;")
            //      div.d-flex.align-center.fill-height
            //        div.text-sm.font-semibold.neutral700--text.mx-3.text-no-wrap Available
            //    v-text-field.text-sm(:value="stockSummaryDisplay.available" placeholder="0" background-color="neutral300" disabled outlined dense height="48" hide-details style="border-radius: 0 10px 10px 0;")
            //      template(#append v-if="uomConversionEnabled && stockUomEquivalents.available.length > 0")
            //        yz-tooltip(direction="top" max-width="250")
            //          yz-icon.mt-2.cursor-pointer(size="18") mdi-information-outline
            //          template(#tooltip)
            //            .yz-heading-9.white--text Equivalent to
            //            ul.pl-4.mt-1
            //              li.yz-regular-body-sm.white--text(v-for="(equiv, idx) in stockUomEquivalents.available" :key="idx") {{ equiv }}
            v-col(cols="6")
              div.d-flex(style="height: 48px;")
                div.button-box(style="min-width: 130px;")
                  div.d-flex.align-center.fill-height
                    div.text-sm.font-semibold.neutral700--text.mx-3.text-no-wrap Default UOM
                v-text-field.text-sm(v-if="isHqProduct" :value="medicine.uom || '-'" background-color="neutral300" disabled outlined dense height="48" hide-details style="border-radius: 0 10px 10px 0;")
                medication-unit-of-measure-dropdown.flex-grow-1(v-else v-model="medicine.uom" attach field-min-width="0" :field-style="defaultUomFieldStyle" :error="false" :allowed-values="uomAllowedValues" :deletable="!uomConversionEnabled" :searchable="!uomConversionEnabled" @input="uomError = false")
              .text-xs.danger500--text.mt-1(v-if="uomError" style="padding-left: 130px;") Default UOM must be one of the values from the UOM conversion list

          template(v-if="!id")
            .text-md.font-semibold.neutral700--text.mt-6 Add your initial stock
            v-row.mt-3
              v-col(cols="3")
                div.text-sm.font-semibold.neutral500--text Stock quantity
                v-text-field.text-sm.mt-2.round-xl(v-model="stock.change" placeholder="0" outlined dense height="48" type="number" :rules="hasStock ? rules.has_price : []" hide-details="auto" @input="updateCost")
              v-col(cols="3")
                div.text-sm.font-semibold.neutral500--text Stock cost
                v-text-field.text-sm.mt-2.round-xl(v-model="stock.cost" placeholder="100" outlined dense height="48" type="number" :prefix="$currency.symbol" :rules="hasStock ? rules.has_price : []" hide-details="auto" @input="updateCost")
              v-col(cols="3")
                div.text-sm.font-semibold.neutral500--text Cost per item
                v-text-field.text-sm.mt-2.round-xl(v-model="medicine.cost" placeholder="0.00" background-color="neutral300" outlined dense height="48" type="number" :prefix="$currency.symbol" disabled hide-details="auto")
              v-col(cols="3")
                div.text-sm.font-semibold.neutral500--text Batch number
                v-text-field.text-sm.mt-2.round-xl(v-model="stock.batch_no" placeholder="Batch number" outlined dense height="48" hide-details="auto")
            div
              //- template(v-if="!addInvoice")
                div.text-sm.font-semibold.neutral500--text.mt-6 Invoice
                v-row.mt-0( align="center")
                  v-col(cols="8")
                    invoice-dropdown(v-model="invoice" attach show-person-in-charge)
                  v-col(cols="1")
                    div.text-sm.font-semibold.neutral700--text.text-center OR
                  v-col
                    y-button(label="Add new invoice" underline color="neutral700" size="md" button-class="px-3" @click="changeInvoiceSelection(true)")
              //- template(v-else)
                div.d-flex.align-center.mt-4
                  div.text-sm.font-semibold.neutral500--text Invoice
                  v-spacer
                  y-button(label="Select from existing invoice" underline color="neutral700" size="sm" button-class="px-3" @click="changeInvoiceSelection(false)")
                v-row.mt-0
                  v-col
                    div.text-sm.font-semibold.neutral500--text Invoice number
                    v-text-field.text-sm.mt-2.round-xl(v-model="invoiceNo" placeholder="Invoice number" outlined dense height="48" :rules="rules.not_empty" hide-details="auto")
                  v-col
                    div.text-sm.font-semibold.neutral500--text Invoice date
                    y-dialog(v-model="showInvoicePicker" width='320px' persistent)
                      template(v-slot:activator='{ on }')
                        v-text-field.text-sm.mt-2.round-xl.date-field(v-model="invoiceDateField" placeholder="Invoice date" outlined dense height="48" readonly :rules="rules.not_empty" hide-details="auto" v-on="on" clearable)
                          template(v-slot:append)
                            eva-icon(name="calendar-outline" fill="#848484")
                      v-card
                        v-card-text.px-2.pt-2.pb-0
                          yc-date-picker(:parentDate.sync="selectedInvoiceDate" color="info")
                        v-card-actions.px-4.pb-4
                          v-row(no-gutters)
                            v-col.pr-2
                              v-btn.button-sm(color="basic700" block dark outlined depressed @click="showInvoicePicker = false") {{ $t('cancel') }}
                            v-col.pl-2
                              v-btn.button-sm(color="info500" block depressed dark @click="pickDate('invoiceDateField', 'showInvoicePicker', selectedInvoiceDate)") {{ $t('select') }}
                  v-col
                    div.text-sm.font-semibold.neutral500--text Sold by
                    medication-sold-by-dropdown.mt-2(v-model="seller" attach show-add-seller :error="showSellerError" @closed="showSellerError = false")

          v-row.mt-3
            v-col
              div.text-sm.font-semibold.neutral500--text Stock warning amount
              v-text-field.text-sm.mt-2.round-xl(v-model="medicine.stock_amount_warning" placeholder="Stock amount" type="number" outlined dense height="48" hide-details="auto")
            v-col
              div.text-sm.font-semibold.neutral500--text Stock expiry warning
              v-text-field.text-sm.mt-2.round-xl(v-model="medicine.stock_expiry_warning" placeholder="0" type="number" outlined dense height="48" suffix="days" hide-details="auto")
            v-col(v-if="!id")
              div.text-sm.font-semibold.neutral500--text Expiry date
              y-dialog(v-model="showExpiredPicker" width='320px' persistent)
                template(v-slot:activator='{ on }')
                  v-text-field.text-sm.mt-2.round-xl.date-field(v-model="expiryDateField" placeholder="Expiry date" outlined dense height="48" readonly hide-details="auto" v-on="on" clearable)
                    template(v-slot:append)
                      eva-icon(name="calendar-outline" fill="#848484")
                v-card
                  v-card-text.px-2.pt-2.pb-0
                    yc-date-picker(:parentDate.sync="selectedExpiryDate" color="info")
                  v-card-actions.px-4.pb-4
                    v-row(no-gutters)
                      v-col.pr-2
                        v-btn.button-sm(color="basic700" block dark outlined depressed @click="showExpiredPicker = false") {{ $t('cancel') }}
                      v-col.pl-2
                        v-btn.button-sm(color="info500" block depressed dark @click="pickDate('expiryDateField', 'showExpiredPicker', selectedExpiryDate)") {{ $t('select') }}

          v-row.mt-3(v-if="id")
            v-col(cols="6")
              div.text-sm.font-semibold.neutral500--text Latest cost price per item
              v-text-field.text-sm.mt-2.round-xl(:value="medicine.last_price" placeholder="0.00" background-color="neutral300" outlined dense height="48" :prefix="$currency.symbol" :suffix="medicine.uom" disabled hide-details="auto")
            v-col(cols="6")
              div.text-sm.font-semibold.neutral500--text Average cost price per item
              v-text-field.text-sm.mt-2.round-xl(:value="medicine.cost" placeholder="0.00" background-color="neutral300" outlined dense height="48" :prefix="$currency.symbol" :suffix="medicine.uom" disabled hide-details="auto")

        //- Location
        v-card.pa-6.mt-4.round-xl(outlined)
          div.d-flex.justify-space-between
            div
              .text-md.font-semibold.neutral700--text Assign this inventory into a location
              .text-sm.neutral700--text.mt-2 Assigning you inventory to a location will help you in your stock take. Your inventory will be organised according to the location you assigned
            div
              y-switch.mb-n3.mt-2(v-model="locationEnabled")
          v-row.mt-3(v-if="locationEnabled")
            v-col(cols="6")
              div.text-sm.font-semibold.neutral500--text Choose location
              medication-location-dropdown.mt-2(v-model="medicine.location" :error="false")

        //- Supplier details
        v-card.pa-6.mt-4.round-xl(outlined)
          supplier-details-section(v-model="medicine.supplier" :company-id="company.id" :disabled="!canEditSupplier" delete-description="Removing this supplier will clear supplier details for this item. Click 'Confirm' to proceed." @supplier-deleted="onSupplierDeleted")

        //- Remarks
        v-card.pa-6.mt-4.round-xl(outlined)
          .text-md.font-semibold.neutral700--text Remarks
          v-textarea.text-sm.mt-2.round-xl(v-model="medicine.remarks" placeholder="Write your remarks here" outlined rows="5" hide-details="auto")

  v-footer.white(v-if="!isFetching" app inset padless elevation="3")
    v-container.px-8.py-2
      div.d-flex.align-center
        v-spacer
        y-button.mr-4(:label="$t('cancel')" underline color="neutral700" size="md" button-class="px-0" @click="goBack")
        y-button(:label="$t('save')" text-color="neutral100" color="neutral700" size="md" dark :loading="isLoading" @click="saveData")

  .dialogs
    update-stock-dialog(v-if="openUpdateStockDialog" :open.sync="openUpdateStockDialog" :stock-count="stock.change" :product="product" @update="updateStockCount($event)" @closed="openUpdateStockDialog = false")
    pricing-tier-details-dialog(v-if="openPricingTierDetailsDialog" :open.sync="openPricingTierDetailsDialog" :tier="selectedTier" @closed="selectedTier = null")
    uom-conversion-dialog(v-model="showUomConversionDialog" :default-uom="medicine.uom" :conversions="uomConversions" :original-base-uom="originalUom" :current-stock-on-hand="stock.change || 0" :company-slug="company.slug" :medication-id="id" :api-endpoint="`/clinics/${company.slug}/medications/${id}`" @save="onSaveUomConversions" @base-uom-change-requested="onBaseUomChangeRequested")
    override-pricing-dialog(v-if="showOverridePricingDialog" :open.sync="showOverridePricingDialog" :branches="[currentBranchForOverride]" :price-tiers="mappedHqPriceTiers" :uom-conversions="uomConversions" :uom-conversion-enabled="uomConversionEnabled" :default-uom="medicine.uom" :currency="$currency" :existing-override="currentOverrideForDialog" hide-branch-selection @save="onSaveOverride" @closed="onCloseOverrideDialog")
    cms-os-v2-consent-dialog(v-if="showCmsOsV2ConsentDialog" :open.sync="showCmsOsV2ConsentDialog" @consented="onCmsOsV2Consented")
    package-archive-dialog(v-model="showPackageArchiveDialog" :packages="packagesToArchive" :loading="isArchivingPackages" @confirmed="onConfirmArchivePackages" @closed="onCloseArchiveDialog")
    base-uom-change-dialog(v-model="showBaseUomChangeDialog" :original-uom="originalUom" :new-uom="pendingBaseUomData ? pendingBaseUomData.newBaseUom : null" :current-stock-on-hand="stock.change || 0" :company-slug="company.slug" :medication-id="id" :uom-conversions="pendingBaseUomData ? pendingBaseUomData.conversions : []" :api-endpoint="`/clinics/${company.slug}/medications/${id}`" @saved="onBaseUomSaved")
    enable-uom-conversion-dialog(v-model="showEnableUomConversionDialog" :previous-uom="enableUomConversionData ? enableUomConversionData.previousUom : null" :new-base-uom="enableUomConversionData ? enableUomConversionData.newBaseUom : null" :default-uom="medicine.uom" :multiplier="enableUomConversionData ? enableUomConversionData.multiplier : 1" :current-stock-on-hand="stock.change || 0" :company-slug="company.slug" :medication-id="id" :uom-conversions="uomConversions" :api-endpoint="`/clinics/${company.slug}/medications/${id}`" @confirmed="onEnableUomConversionConfirmed")

</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { buildUomGraph, getUomScale } from '@/utils'
import DeleteConfirmationDialog from '@/components/clinic/dialogs/DeleteConfirmationDialog.vue'
import PricingTierDetailsDialog from '@/components/clinic/inventory/PricingTierDetailsDialog.vue'
import MedicationGroupDropdown from '@/components/clinic/inventory/MedicationGroupDropdown.vue'
import MedicationSkuDropdown from '@/components/clinic/inventory/MedicationSkuDropdown.vue'
import MedicationDosageUnitDropdown from '@/components/clinic/inventory/MedicationDosageUnitDropdown.vue'
import MedicationInstructionDropdown from '@/components/clinic/inventory/MedicationInstructionDropdown.vue'
import MedicationPrecautionDropdown from '@/components/clinic/inventory/MedicationPrecautionDropdown.vue'
import MedicationFrequencyDropdown from '@/components/clinic/inventory/MedicationFrequencyDropdown.vue'
import MedicationDurationDropdown from '@/components/clinic/inventory/MedicationDurationDropdown.vue'
import MedicationSoldByDropdown from '@/components/clinic/inventory/MedicationSoldByDropdown.vue'
import MedicationIndicationDropdown from '@/components/clinic/inventory/MedicationIndicationDropdown.vue'
import MedicationUnitOfMeasureDropdown from '@/components/clinic/inventory/MedicationUnitOfMeasureDropdown.vue'
import MedicationLocationDropdown from '@/components/clinic/inventory/MedicationLocationDropdown.vue'
import InvoiceDropdown from '@/components/clinic/inventory/InvoiceDropdown.vue'
import UpdateStockDialog from '@/components/clinic/inventory/UpdateStockDialog.vue'
import UomConversionDialog from '@/components/hq/inventory/branches-warehouses/dialog/UomConversionDialog.vue'
import SupplierDetailsSection from '@/components/hq/inventory/branches-warehouses/dialog/SupplierDetailsSection.vue'
import OverridePricingDialog from '@/components/hq/inventory/branches-warehouses/dialog/OverridePricingDialog.vue'
import CmsOsV2ConsentDialog from '@/components/clinic/dialogs/CmsOsV2ConsentDialog.vue'
import PackageArchiveDialog from '@/components/hq/inventory/branches-warehouses/dialog/PackageArchiveDialog.vue'
import BaseUomChangeDialog from '@/components/hq/inventory/branches-warehouses/dialog/BaseUomChangeDialog.vue'
import EnableUomConversionDialog from '@/components/hq/inventory/branches-warehouses/dialog/EnableUomConversionDialog.vue'

export default {
  name: 'Medication',
  components: {
    DeleteConfirmationDialog, PricingTierDetailsDialog, MedicationGroupDropdown, MedicationSkuDropdown, MedicationDosageUnitDropdown, MedicationInstructionDropdown,
    MedicationPrecautionDropdown, MedicationFrequencyDropdown, MedicationDurationDropdown, MedicationSoldByDropdown, MedicationIndicationDropdown,
    MedicationUnitOfMeasureDropdown, MedicationLocationDropdown, InvoiceDropdown, UpdateStockDialog, UomConversionDialog, SupplierDetailsSection, OverridePricingDialog, CmsOsV2ConsentDialog, PackageArchiveDialog, BaseUomChangeDialog, EnableUomConversionDialog
  },
  props: ['id', 'savePage', 'saveTab', 'saveSearch', 'saveFilters'],
  data () {
    return {
      isFetching: false,
      isFetchingPricingTiers: false,
      openPricingTierDetailsDialog: false,
      openUpdateStockDialog: false,
      isLoading: false,
      addInvoice: false,
      instructionEnabled: false,
      locationEnabled: false,
      uomConversionEnabled: false,
      uomError: false,
      showUomConversionDialog: false,
      showCmsOsV2ConsentDialog: false,
      uomConversions: [],
      originalUom: null,
      originalDefaultUom: null,
      originalUomConversionEnabled: false,
      originalUomConversions: [],
      showPackageArchiveDialog: false,
      packagesToArchive: [],
      isCheckingPackages: false,
      isArchivingPackages: false,
      showBaseUomChangeDialog: false,
      pendingBaseUomData: null,
      archiveFlowSource: null,
      showEnableUomConversionDialog: false,
      enableUomConversionData: null,
      showInvoicePicker: false,
      showExpiredPicker: false,
      showSellerError: false,
      selectedTier: null,
      invoiceNo: null,
      selectedInvoiceDate: null,
      selectedExpiryDate: null,
      invoiceDateField: null,
      expiryDateField: null,
      pageSize: -1,
      invoice: {},
      product: {},
      hqPriceTiers: [],
      showOverridePricingDialog: false,
      overridePriceTiers: [],
      medicine: {},
      seller: {},
      stock: {},
      stockSummaryData: { inventory_qty: 0, available_qty: 0, committed_qty: 0, reserved_qty: 0 },
      menus: [ { title: 'haha' } ],
      headers: [
        { text: 'Name', value: 'name', enable: true, class: 'text-table-header' },
        { text: 'Payment methods', value: 'payment', enable: true, class: 'text-table-header' },
        { text: 'Price', value: 'price', enable: true, class: 'text-table-header' },
      ],
      pricingTiers: [],
      rules: {
        not_empty: [v => (v != null && v.toString().trim().length) || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 0 || `Amount should be more than ${this.$currency.symbol} 0`],
        not_empty_select: [v => !!v || 'This field is required']
      },
    }
  },
  computed: {
    ...mapState('user', ['member']),
    ...mapGetters({
      company: 'getCompany',
      isDoctor: 'user/isDoctor',
    }),
    ...mapGetters('hq', ['canOverridePrice', 'canOverrideSupplier']),
    hasStock () {
      if (Object.keys(this.stock).length === 0) {
        return false
      } else if (Object.values(this.stock).every(val => !val)) {
        return false
      }
      return true
    },
    isAdmin () {
      return this.member.roles.includes('Admin')
    },
    isAdminOrDoctor () {
      if (this.isAdmin || this.isDoctor) return true
      return false
    },
    isHqProduct () {
      return !!this.product.branch_for
    },
    isBranchHqProduct () {
      return this.isHqProduct && !!this.company?.branch_of
    },
    canEditSupplier () {
      if (!this.isBranchHqProduct) return true
      return this.canOverrideSupplier
    },
    canEditPriceOverride () {
      if (!this.isBranchHqProduct) return true
      return this.canOverridePrice
    },
    groupNames () {
      if (!this.medicine.groups || !this.medicine.groups.length) return '-'
      return this.medicine.groups.map(g => g.value || g).join(', ')
    },
    hasActiveUomConversions () {
      return this.uomConversionEnabled && this.uomConversions.length > 0
    },
    // Triggered only in edit mode when UOM conversion is toggled ON (was OFF) and new base UOM != previously persisted default UOM.
    // previousUom is the persisted default UOM (originalDefaultUom) — used as the dialog label and for the trigger comparison.
    // The multiplier is computed from medicine.uom (the user's currently-selected UOM in the new chain) → newBaseUom,
    // since previousUom may not exist in the new conversion graph.
    needsEnableUomConversionFlow () {
      if (!this.id) return null
      if (this.originalUomConversionEnabled || !this.uomConversionEnabled) return null
      if (!this.uomConversions || this.uomConversions.length === 0) return null
      const newBaseUom = this.uomConversions[this.uomConversions.length - 1].convert_to
      const previousUom = this.originalDefaultUom
      if (!previousUom || !newBaseUom || previousUom === newBaseUom) return null
      // Multiplier from currently-selected UOM (medicine.uom) to new base UOM.
      // Case 1 (selected === base, or not in graph): multiplier = 1 (qty unchanged, label only).
      // Case 2 (selected is elsewhere in graph): multiplier = scale(selected) / scale(base).
      const currentSelectedUom = this.medicine.uom
      const graph = buildUomGraph(this.uomConversions)
      let multiplier = 1
      if (currentSelectedUom && currentSelectedUom !== newBaseUom && graph[currentSelectedUom] != null && graph[newBaseUom] != null) {
        const selectedScale = getUomScale(graph, currentSelectedUom)
        const baseScale = getUomScale(graph, newBaseUom)
        if (baseScale > 0) multiplier = selectedScale / baseScale
      }
      return { previousUom, newBaseUom, multiplier }
    },
    uomAllowedValues () {
      if (!this.uomConversionEnabled || this.uomConversions.length === 0) return null
      const uoms = new Set()
      this.uomConversions.forEach(c => { uoms.add(c.uom); uoms.add(c.convert_to) })
      return [...uoms]
    },
    defaultUomFieldStyle () {
      const base = 'border-radius: 0 10px 10px 0 !important;'
      return this.uomError ? `${base} border-color: var(--v-danger500) !important;` : base
    },
    defaultUomScale () {
      if (!this.uomConversionEnabled || this.uomConversions.length === 0) return 1
      const graph = buildUomGraph(this.uomConversions)
      return getUomScale(graph, this.medicine.uom)
    },
    stockSummary () {
      if (this.id) {
        // While EnableUomConversion flow is pending (toggled ON locally, not yet saved),
        // inventory_qty is still stored in originalDefaultUom — skip the local scale math.
        if (this.needsEnableUomConversionFlow) {
          const raw = this.stockSummaryData.inventory_qty || 0
          const rawAvail = this.stockSummaryData.available_qty || 0
          return { stockOnHand: raw, available: rawAvail }
        }
        const scale = this.defaultUomScale
        const stockOnHand = (this.stockSummaryData.inventory_qty || 0) / scale
        const available = (this.stockSummaryData.available_qty || 0) / scale
        return { stockOnHand, available }
      }
      const stockOnHand = Number(this.stock.change) || 0
      return { stockOnHand, available: stockOnHand }
    },
    stockSummaryDisplay () {
      // When the enable-conversion flow is pending, display in originalDefaultUom (the persisted UOM).
      const uom = this.needsEnableUomConversionFlow ? (this.originalDefaultUom || '') : (this.medicine.uom || '')
      const format = (v) => Number.isInteger(v) ? `${v}` : parseFloat(v.toFixed(2)).toString()
      const onHand = format(this.stockSummary.stockOnHand)
      const available = format(this.stockSummary.available)
      return {
        stockOnHand: uom ? `${onHand} ${uom}` : onHand,
        available: uom ? `${available} ${uom}` : available
      }
    },
    stockUomEquivalents () {
      // Hide equivalents tooltip while EnableUomConversion flow is pending — the conversion
      // hasn't been persisted yet so any locally-computed equivalents would be misleading.
      if (this.needsEnableUomConversionFlow) {
        return { stockOnHand: [], available: [] }
      }
      if (!this.uomConversionEnabled || this.uomConversions.length === 0) {
        return { stockOnHand: [], available: [] }
      }
      const rawOnHand = this.id ? this.stockSummaryData.inventory_qty : this.stockSummary.stockOnHand * this.defaultUomScale
      const rawAvailable = this.id ? this.stockSummaryData.available_qty : this.stockSummary.available * this.defaultUomScale
      const graph = buildUomGraph(this.uomConversions)
      const selectedUom = this.medicine.uom
      const formatValue = (v) => Number.isInteger(v) ? `${v}` : parseFloat(v.toFixed(2)).toString()
      const buildEquivalents = (rawBaseQty) => {
        if (!rawBaseQty) return []
        return Object.keys(graph)
          .filter(uom => uom !== selectedUom)
          .map(uom => ({ uom, scale: getUomScale(graph, uom), value: rawBaseQty / getUomScale(graph, uom) }))
          .filter(({ value }) => Number(value.toFixed(2)) > 0)
          .sort((a, b) => b.scale - a.scale)
          .map(({ uom, value }) => `${formatValue(value)} ${uom}`)
      }
      return {
        stockOnHand: buildEquivalents(rawOnHand),
        available: buildEquivalents(rawAvailable)
      }
    },
    allowedPriceEdit () {
      // create inventory can edit price
      if (!this.id) return true
      // admin can edit price
      if (this.isAdmin) return true
      // 'lock_treatment_plan' is for CA
      if (this.company.genes.find(gene => gene.feature === 'lock_treatment_plan') && !this.isAdminOrDoctor) return false
      // 'lock_treatment_plan_price_edit' is for doctor
      if (this.company.genes.find(gene => gene.feature === 'lock_treatment_plan_price_edit') && this.isDoctor) return false
      return true
    },
    currentBranchForOverride () {
      return { companyId: this.company.id, name: this.company.name, enabled: true }
    },
    mappedHqPriceTiers () {
      return this.hqPriceTiers.map(hqTier => {
        const tier = hqTier.price_tier
        return {
          id: tier.id,
          name: tier.name,
          description: tier.description,
          panelText: this.getHqTierPanelCount(tier),
          selfPayText: this.getHqTierSelfPayCount(tier),
          price: hqTier.price ? (hqTier.price / this.$currency.multiplier).toFixed(2) : null,
          price_per_uom: (hqTier.price_per_uom || []).map(p => ({ uom: p.uom, price: p.price ? (p.price / this.$currency.multiplier).toFixed(2) : null }))
        }
      })
    },
    currentOverrideForDialog () {
      if (this.overridePriceTiers.length === 0) return null
      return {
        branches: [this.company.id],
        priceTiers: this.overridePriceTiers.map(t => ({
          price_tier: t.price_tier,
          price: t.price,
          price_per_uom: t.price_per_uom || []
        }))
      }
    },
    formattedOverrideTiers () {
      return this.overridePriceTiers.map(oTier => {
        const hqTier = this.hqPriceTiers.find(h => h.price_tier.id === oTier.price_tier)
        const tier = hqTier ? hqTier.price_tier : {}
        return {
          ...oTier,
          name: tier.name || '',
          description: tier.description || '',
          panelText: this.getHqTierPanelCount(tier),
          selfPayText: this.getHqTierSelfPayCount(tier),
          tierObj: tier
        }
      })
    },
  },
  watch: {
    uomConversions () {
      this.refreshTierUomPrices()
    }
  },
  mounted () {
    if (this.id) {
      this.fetchMedication()
    } else {
      this.fetchPricingTiers()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchMedication () {
      this.isFetching = true
      return this.$axios.get(`/clinics/${this.company.slug}/medications/${this.id}`)
        .then(resp => {
          this.medicine = {
            name: resp.data.product.name,
            remarks: resp.data.product.description,
            category: resp.data.category,
            generic_name: resp.data.generic_name,
            groups: resp.data.groups,
            sku: resp.data.product.sku || null,
            dosage_value: resp.data.dosage_value,
            dosage_unit: resp.data.dosage_unit,
            instruction: resp.data.instruction,
            precaution: resp.data.precaution,
            frequency: resp.data.frequency,
            duration: resp.data.duration,
            indication: resp.data.indication,
            uom: resp.data.product.uom,
            location: resp.data.product.location,
            default_qty: resp.data.default_qty,
            stock_amount_warning: resp.data.product.stock_amount_warning?.toString() || null,
            stock_expiry_warning: resp.data.product.stock_expiry_warning?.toString() || null,
            cost: resp.data.product.cost != null ? resp.data.product.cost / this.$currency.multiplier : null,
            last_price: resp.data.product.last_price != null ? resp.data.product.last_price / this.$currency.multiplier : null,
            supplier: resp.data.product.supplier || null
          }
          this.instructionEnabled = resp.data.instruction_enabled
          this.uomConversionEnabled = !!resp.data.product.uom_conversion_enabled
          const conversions = resp.data.product.uom_conversions || []
          this.uomConversions = conversions
          this.originalUom = conversions.length > 0 ? conversions[conversions.length - 1].convert_to : null
          this.originalDefaultUom = resp.data.product.uom || null
          this.originalUomConversionEnabled = !!resp.data.product.uom_conversion_enabled
          this.originalUomConversions = JSON.parse(JSON.stringify(conversions))
          this.locationEnabled = Boolean(resp.data.product.location)
          this.stock.change = resp.data.product.inventory_qty
          this.stockSummaryData = {
            inventory_qty: resp.data.product.inventory_qty || 0,
            available_qty: resp.data.product.available_qty != null ? resp.data.product.available_qty : (resp.data.product.inventory_qty || 0),
            committed_qty: resp.data.product.committed_qty || 0,
            reserved_qty: resp.data.product.reserved_qty || 0
          }
          this.product = resp.data.product
          if (this.isHqProduct && this.company?.branch_of) {
            this.$store.dispatch('hq/fetchInventorySettings', { axios: this.$axios, hqId: this.company.branch_of.id })
          }
          this.hqPriceTiers = resp.data.hq_price_tiers || []
          // Extract branch override pricing from the branch's own price_tiers
          const branchPriceTiers = resp.data.price_tiers || []
          if (this.isHqProduct && branchPriceTiers.length > 0) {
            const multiplier = Number(this.$currency.multiplier)
            this.overridePriceTiers = branchPriceTiers.map(pt => ({
              price_tier: pt.price_tier?.id || pt.price_tier,
              price: pt.price ? (pt.price / multiplier).toFixed(2) : null,
              price_per_uom: (pt.price_per_uom || []).map(p => ({ uom: p.uom, price: p.price ? (p.price / multiplier).toFixed(2) : null })),
              expanded: false
            }))
          }
          this.fetchPricingTiers(this.isHqProduct ? null : resp.data.price_tiers)
          this.isFetching = false
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    getUomList () {
      if (!this.uomConversionEnabled || this.uomConversions.length === 0) return []
      const uoms = []
      this.uomConversions.forEach(c => {
        if (!uoms.includes(c.uom)) uoms.push(c.uom)
        if (!uoms.includes(c.convert_to)) uoms.push(c.convert_to)
      })
      return uoms
    },
    refreshTierUomPrices () {
      const uomList = this.getUomList()
      this.pricingTiers.forEach(tier => {
        const existing = tier.price_per_uom || []
        tier.price_per_uom = uomList.map(uom => {
          const found = existing.find(p => p.uom === uom)
          return { uom, price: found ? found.price : null }
        })
      })
    },
    tierHasPrice (tier) {
      if (!this.hasActiveUomConversions) return tier.price !== null && tier.price !== ''
      return tier.price_per_uom.some(p => p.price !== null && p.price !== '')
    },
    fetchPricingTiers (pricetiers) {
      this.isFetchingPricingTiers = true
      const params = {
        company: this.company.id,
        status: 'active',
        page_size: this.pageSize
      }
      this.$axios.get('/product_pricetiers', { params })
        .then(resp => {
          if (resp.data && resp.data.results) {
            const uomList = this.getUomList()
            const multiplier = Number(this.$currency.multiplier)
            if (pricetiers) {
              let data = []
              resp.data.results.forEach(o => {
                let ptier = pricetiers.find(f => f.price_tier.id === o.id)
                o.price = null
                o.price_per_uom = uomList.map(uom => ({ uom, price: null }))
                o.expanded = false
                if (ptier) {
                  o.price = ptier.price / multiplier
                  o.required = true
                  if (ptier.price_per_uom && ptier.price_per_uom.length > 0) {
                    ptier.price_per_uom.forEach(savedUom => {
                      const uomEntry = o.price_per_uom.find(p => p.uom === savedUom.uom)
                      if (uomEntry) uomEntry.price = (savedUom.price / multiplier).toFixed(2)
                    })
                  }
                }
                data.push(o)
              })
              this.pricingTiers = data
            } else {
              this.pricingTiers = resp.data.results.map(m => { return { ...m, price: null, required: false, price_per_uom: uomList.map(uom => ({ uom, price: null })), expanded: false } })
            }
          }
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingPricingTiers = false
        })
    },
    goToPriceTiers () {
      this.$router.push('/admin/clinic/settings/price-tier-settings')
    },
    goToStockHistory () {
      let route = {
        name: 'StockHistory',
        params: {
          origin: 'EditClinicMedication',
          data: { params: { ...this.$route.params } }
        }
      }
      this.$router.push(route)
    },
    async saveData () {
      if (!this.$refs.form.validate()) {
        this.showMessage(['error', this.$t('error_message.please_fill_out_all_required')])
        return
      }
      if (this.uomConversionEnabled && this.uomAllowedValues && !this.uomAllowedValues.includes(this.medicine.uom)) {
        this.uomError = true
        this.showMessage(['error', 'Default UOM must be one of the values from the UOM conversion list'])
        return
      }
      this.uomError = false
      // Show enable-conversion confirmation BEFORE cmsOsV2 + package-archive checks;
      // user must confirm auto-conversion of existing stock to the new base unit.
      const enableFlow = this.needsEnableUomConversionFlow
      if (enableFlow) {
        this.enableUomConversionData = enableFlow
        this.showEnableUomConversionDialog = true
        return
      }
      if (this.uomConversionEnabled) {
        const cmsOsV2 = await this.$store.dispatch('clinic/fetchCmsOsV2', this.$axios)
        if (!cmsOsV2) {
          this.showCmsOsV2ConsentDialog = true
          return
        }
      }
      this.checkPackagesToArchive()
    },
    onCmsOsV2Consented () {
      this.checkPackagesToArchive()
    },
    async onEnableUomConversionConfirmed () {
      // PATCH already persisted the base_uom_conversion; sync original* state so downstream
      // change-detection (hasUomConversionChanged) sees no further change in this save flow.
      this.showEnableUomConversionDialog = false
      this.originalUomConversionEnabled = true
      this.originalUom = this.medicine.uom
      this.originalDefaultUom = this.medicine.uom
      this.originalUomConversions = JSON.parse(JSON.stringify(this.uomConversions))
      this.enableUomConversionData = null
      if (this.uomConversionEnabled) {
        const cmsOsV2 = await this.$store.dispatch('clinic/fetchCmsOsV2', this.$axios)
        if (!cmsOsV2) {
          this.showCmsOsV2ConsentDialog = true
          return
        }
      }
      this.checkPackagesToArchive()
    },
    hasUomConversionChanged () {
      if (!this.id) return { changed: false }
      const collectUomNames = (conversions) => {
        const names = new Set()
        conversions.forEach(c => { names.add(c.uom); names.add(c.convert_to) })
        return names
      }
      const wasEnabled = this.originalUomConversionEnabled
      const isEnabled = this.uomConversionEnabled
      // Case 1: toggled ON (was off → now on)
      if (!wasEnabled && isEnabled) return { changed: true, removedUoms: [] }
      // Case 2: toggled OFF (was on → now off)
      if (wasEnabled && !isEnabled) {
        const originalNames = [...collectUomNames(this.originalUomConversions)]
        return { changed: true, removedUoms: originalNames }
      }
      // Case 3: still enabled, check if names changed
      if (wasEnabled && isEnabled) {
        const originalNames = collectUomNames(this.originalUomConversions)
        const currentNames = collectUomNames(this.uomConversions)
        const removedUoms = [...originalNames].filter(name => !currentNames.has(name))
        if (removedUoms.length > 0) return { changed: true, removedUoms }
      }
      // Case 4: no relevant changes
      return { changed: false }
    },
    async checkBaseUomPackages (oldUom) {
      if (!oldUom) return
      this.isCheckingPackages = true
      try {
        const productId = this.product.id
        const resp = await this.$axios.get(`/clinics/${this.company.slug}/packages`, {
          params: { status: 'published', item_uoms: `${productId},${oldUom}`, fields: 'id,product,treatments' }
        })
        const packages = resp.data?.results || resp.data || []
        if (packages.length > 0) {
          this.archiveFlowSource = 'base-uom-change'
          this.packagesToArchive = packages
          this.showPackageArchiveDialog = true
        } else {
          this.showBaseUomChangeDialog = true
        }
      } catch {
        this.showMessage(['error', 'Unable to check affected packages. Please try again.'])
      } finally {
        this.isCheckingPackages = false
      }
    },
    async checkPackagesToArchive () {
      const result = this.hasUomConversionChanged()
      if (!result.changed) {
        this.proceedWithSave()
        return
      }
      this.isCheckingPackages = true
      try {
        const itemUoms = result.removedUoms.length > 0
          ? `${this.product.id},${result.removedUoms.join(',')}`
          : `${this.product.id}`
        const resp = await this.$axios.get(`/clinics/${this.company.slug}/packages`, {
          params: { status: 'published', item_uoms: itemUoms, fields: 'id,product,treatments' }
        })
        const packages = resp.data?.results || resp.data || []
        if (packages.length > 0) {
          this.packagesToArchive = packages
          this.showPackageArchiveDialog = true
        } else {
          this.proceedWithSave()
        }
      } catch {
        this.showMessage(['error', 'Unable to check affected packages. Please try again.'])
      } finally {
        this.isCheckingPackages = false
      }
    },
    async onConfirmArchivePackages () {
      this.isArchivingPackages = true
      try {
        const ids = this.packagesToArchive.map(p => p.id)
        await this.$axios.post(`/clinics/${this.company.slug}/packages/bulk_archive`, { ids })
        this.showPackageArchiveDialog = false
        if (this.archiveFlowSource === 'base-uom-change') {
          this.archiveFlowSource = null
          this.showBaseUomChangeDialog = true
        } else {
          this.proceedWithSave()
        }
      } catch {
        this.showMessage(['error', 'Failed to archive packages. Please try again.'])
      } finally {
        this.isArchivingPackages = false
      }
    },
    onCloseArchiveDialog () {
      this.showPackageArchiveDialog = false
      this.packagesToArchive = []
      this.archiveFlowSource = null
      this.pendingBaseUomData = null
    },
    proceedWithSave () {
      this.isLoading = true
      const multiplier = Number(this.$currency.multiplier)
      let data = {
        product: {
          name: this.medicine.name,
          description: this.medicine.remarks,
          sku: this.medicine.sku || null,
          stock_amount_warning: this.medicine.stock_amount_warning,
          stock_expiry_warning: this.medicine.stock_expiry_warning,
          uom: this.medicine.uom,
          uom_conversion_enabled: this.uomConversionEnabled,
          supplier: typeof this.medicine.supplier === 'object' ? this.medicine.supplier?.id : this.medicine.supplier || null,
          location: this.locationEnabled && this.medicine.location?.id ? this.medicine.location.id : null
        },
        generic_name: this.medicine.generic_name,
        category: this.medicine.category,
        groups: this.medicine.groups ? this.medicine.groups.map(m => m.id) : [],
        instruction_enabled: this.instructionEnabled,
        dosage_value: this.medicine.dosage_value || null,
        dosage_unit: this.medicine.dosage_unit,
        instruction: this.medicine.instruction,
        precaution: this.medicine.precaution,
        frequency: this.medicine.frequency,
        duration: this.medicine.duration,
        indication: this.medicine.indication,
        default_qty: this.medicine.default_qty,
        price_tiers: this.isHqProduct
          ? this.overridePriceTiers.filter(t => t.price !== null && t.price !== '' || (t.price_per_uom || []).some(p => p.price !== null && p.price !== '')).map(t => ({
              price_tier: t.price_tier,
              price: this.hasActiveUomConversions ? 0 : Math.round(Number(t.price) * multiplier),
              price_per_uom: (t.price_per_uom || []).filter(p => p.price !== null && p.price !== '').map(p => ({ uom: p.uom, price: Math.round(Number(p.price) * multiplier) }))
            }))
          : this.pricingTiers.filter(f => this.tierHasPrice(f)).map(m => ({
              price_tier: m.id,
              price: this.hasActiveUomConversions ? 0 : Math.round(Number(m.price) * multiplier),
              price_per_uom: (m.price_per_uom || []).filter(p => p.price !== null && p.price !== '').map(p => ({ uom: p.uom, price: Math.round(Number(p.price) * multiplier) }))
            })),
      }
      if (this.uomConversionEnabled) {
        data.product.uom_conversions = this.uomConversions.map(c => ({ id: c.id, uom: c.uom, convert_to: c.convert_to, multiplier: c.multiplier }))
      }
      if (this.id) {
        const endpoint = `/clinics/${this.company.slug}/medications/${this.id}`
        this.$axios.patch(endpoint, data)
          .then(resp => {
            this.updateActivityLog(endpoint, data, resp, 'PATCH',`Update medication`, this.id)
            this.goBack()
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          })
          .finally(() => {
            this.isLoading = false
          })
      } else {
        const endpoint = `/clinics/${this.company.slug}/medications`
        this.$axios.post(endpoint, data)
          .then(resp => {
            this.updateActivityLog(endpoint, data, resp, 'POST',`Create new medication`, resp.data.id)
            let product = resp.data.product
            if (this.stock.change) {
              let data2 = {
                ...this.stock,
                cost: Math.round(Number(this.stock.cost) * Number(this.$currency.multiplier)),
                action: 'received',
                expiry_date: this.expiryDateField ? this.selectedExpiryDate : null
              }
              const endpoint2 = `/products/${product.id}/inventories`
              this.$axios.post(endpoint2, data2)
                .then(resp2 => {
                  this.updateActivityLog(endpoint2, data2, resp2, 'POST',`Update stock: received`, resp2.data.id)
                  // let productInventory = resp2.data
                  // if (this.invoice.id) {
                  //   const payload = {
                  //     product_inventory_id: productInventory.id
                  //   }
                  //   this.$axios.post(`/clinics/${this.company.slug}/inventory_invoices/${this.invoice.id}/items/`, payload)
                  //     .catch(() => {
                  //       this.showMessage(['error', 'Error adding item to the selected invoice'])
                  //     })
                  // } else if (this.addInvoice) {
                  //   let payload = {
                  //     invoice_no: this.invoiceNo,
                  //     record_date: this.selectedInvoiceDate,
                  //     seller: this.seller.sold_by,
                  //     product_inventory_ids: [productInventory.id]
                  //   }
                  //   this.$axios.post(`/clinics/${this.company.slug}/inventory_invoices/`, payload)
                  //     .catch(() => {
                  //       this.showMessage(['error', 'Error creating invoice'])
                  //     })
                  // }
                })
                .catch(() => {})
            }
            setTimeout(() => {
              this.goBack()
            }, 1000)
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          })
          .finally(() => {
            this.isLoading = false
          })
      }
    },
    pickDate (field, picker, date) {
      this[field] = this.$options.filters.humanizeDate(date)
      this[picker] = false
    },
    onAddUomConversion () {
      this.showUomConversionDialog = true
    },
    onSaveUomConversions (conversions) {
      this.uomConversions = conversions
    },
    async onBaseUomChangeRequested ({ conversions, newBaseUom }) {
      this.pendingBaseUomData = { conversions, newBaseUom }
      const oldUom = this.medicine.uom
      await this.checkBaseUomPackages(oldUom)
    },
    async onBaseUomSaved () {
      this.showBaseUomChangeDialog = false
      this.showUomConversionDialog = false
      this.uomConversions = this.pendingBaseUomData.conversions
      await this.fetchMedication()
      this.pendingBaseUomData = null
    },
    onSupplierDeleted () {
      this.medicine.supplier = null
    },
    getHqTierPanelCount (tier) {
      const count = tier.panels ? tier.panels.length : 0
      if (count === 0) return 'None included'
      return count + ' included'
    },
    getHqTierSelfPayCount (tier) {
      const count = tier.self_pay ? tier.self_pay.length : 0
      if (count === 0) return 'None included'
      return count + ' included'
    },
    formatOverridePrice (price) {
      if (price === null || price === '' || price === undefined) return '—'
      return `${this.$currency.symbol} ${Number(price).toFixed(2)}`
    },
    onEditOverride () {
      this.showOverridePricingDialog = true
    },
    onSaveOverride (overrideData) {
      this.overridePriceTiers = overrideData.priceTiers.map(t => ({
        price_tier: t.price_tier,
        price: t.price,
        price_per_uom: (t.price_per_uom || []).map(p => ({ uom: p.uom, price: p.price })),
        expanded: false
      }))
      this.onCloseOverrideDialog()
    },
    onCloseOverrideDialog () {
      this.showOverridePricingDialog = false
    },
    openDetails (item) {
      this.selectedTier = item
      this.openPricingTierDetailsDialog = true
    },
    updateStockCount (balance) {
      this.stock.change = balance
      this.openUpdateStockDialog = false
      if (this.id) this.fetchMedication()
    },
    updateCost () {
      let stock = this.stock.change || 0
      let cost = this.stock.cost || 0
      let costPerItem = Number(cost) / (Number(stock) || 1)
      this.$set(this.medicine, 'cost', costPerItem.toFixed(2))
    },
    changeInvoiceSelection (data) {
      this.addInvoice = data
      this.invoice = {}
      this.seller = {}
    },
    getPanelCount (panels) {
      const includedPanel = panels.length
      if (this.pricingTiers.total_panels === includedPanel) {
        return 'All included'
      } else if (includedPanel === 0) {
        return 'None included'
      } else {
        return includedPanel + ' included'
      }
    },
    getSelfPayCount (methods) {
      const includedMethods = methods.length
      if (includedMethods === this.pricingTiers.total_self_pay) {
        return 'All included'
      } else if (includedMethods === 0) {
        return 'None included'
      } else {
        return includedMethods + ' included'
      }
    },
    goBack () {
      let route = { name: 'ClinicInventory' }
      if (this.$route?.params?.origin) {
        route = { name: this.$route.params.origin }
      }
      if (this.$route?.params?.data && Object.keys(this.$route.params.data).length) {
        route = { ...route, ...this.$route.params.data }
      }
      return this.$router.push(route).catch(() => {})
    },
    updateActivityLog (endpoint, payload, response, method, activity, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'inventory',
        activity: activity,
        raw_data: { endpoint, payload, response: response.data, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  } 
  .error--text {
    color: var(--v-danger500) !important;
    fieldset {
      border-color: var(--v-danger500);
    }
  }
  .date-field .v-input__append-inner {
    margin-top: 12px !important;
  }
}
.empty-box {
  position: relative;
  border-radius: 10px;
  border: var(--v-basic500) dashed 2px;
}
.button-box {
  border-left: solid 1px var(--v-neutral400);
  border-top: solid 1px var(--v-neutral400);
  border-bottom: solid 1px var(--v-neutral400);
  border-radius: 10px 0 0 10px;
  background-color: var(--v-neutral200);
}
</style>